/**
 * The bell in the header (LIAA service plan S2.5; P1-14, design Notifications): the unread count, and on
 * click the newest notices — unread ones marked — with "Mark all as read" and "Show older". A notice opens
 * what it is about (a work order) and is marked read. The count refreshes every minute and on focus; no
 * browser push.
 */

import { BellOutlined } from '@ant-design/icons';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Empty, Flex, Popover, Typography } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { apiClient } from '@/shared/api/client';
import { FRESH_WORKLIST_STATE } from '@/shared/lib/freshWorklist';
import { formatPhoneE164 } from '@/shared/lib/phone';

export interface UserNotification {
  id: string;
  kind: string;
  entityKind: string | null;
  entityId: string | null;
  meta: Record<string, string | number | boolean | null>;
  createdAt: string;
  readAt: string | null;
}

const PAGE = 15;
/** Read by a screen reader, not shown (the bold weight says "unread" to the eye). */
const VISUALLY_HIDDEN: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};
const QUIET = { suppressGlobalError: true } as const;
const keys = {
  all: ['notifications'] as const,
  unread: ['notifications', 'unread'] as const,
  list: ['notifications', 'list'] as const,
};

/** The previous page's last notice — its time and id together, as the server orders them. */
interface Cursor {
  at: string;
  id: string;
}

async function fetchPage(
  before: Cursor | null,
  limit: number,
): Promise<{ notifications: UserNotification[]; unread: number }> {
  const { data } = await apiClient.get<{ notifications: UserNotification[]; unread: number }>(
    '/notifications',
    {
      params: { limit, ...(before !== null && { before: before.at, beforeId: before.id }) },
    },
  );
  return data;
}

/** Where a notice leads: its entity's card. */
function hrefOf(n: UserNotification): string | null {
  if (n.entityKind === 'work-order' && n.entityId !== null) return `/work-orders/${n.entityId}`;
  if (n.kind === 'overdue-digest') return '/work-orders';
  // Exit approvals (S8.2): the approver opens the request; the requester the gate record it was asked for.
  if (n.kind === 'exit-approval-requested' && n.entityId !== null) return `/exit-approvals/${n.entityId}`;
  if ((n.kind === 'exit-approval-approved' || n.kind === 'exit-approval-declined') && typeof n.meta.recordId === 'string') {
    return `/gate/records/${n.meta.recordKind === 'storage-agreement' ? 'storage' : 'service'}/${n.meta.recordId}`;
  }
  // Telephony (LMT calls plan §5.7a): the call-back list and the calls journal - by number, not by episode id.
  if (n.kind === 'missed-call') {
    return typeof n.meta.numberE164 === 'string'
      ? `/communications/callbacks?number=${encodeURIComponent(n.meta.numberE164)}`
      : '/communications/callbacks';
  }
  if (n.kind === 'call-needs-customer') return '/communications/calls';
  // Missing deal documents (R18 §4.5): the sales list when a sale owes one ("all" - a commission whose sale
  // side is released is hidden in "open"), else the purchasing view; both filtered to the reader's own deals.
  // The letters about invoices that wait (plan 2026_11_03_reminder_send_queue §2.9): the whole list ("Visas").
  if (n.kind === 'letters-to-send') return '/finance/letters';
  // The export's problems (plan 2026_11_04_jumis_problems §3.6): the problems tab of the Jumis screen.
  if (n.kind === 'jumis-problems') return '/finance/jumis?tab=problems';
  if (n.kind === 'deal-docs-missing') {
    return typeof n.meta.sales === 'number' && n.meta.sales > 0
      ? '/sales?status=all&docs=missing&owner=me'
      : '/inventory/owned-vehicles?view=purchasing&docs=missing&owner=me';
  }
  return null;
}

/** The words a notice needs beyond its stored meta: an instant shown as the reader's day and time. */
function wordsOf(n: UserNotification): Record<string, unknown> {
  const words: Record<string, unknown> = { ...n.meta };
  if (typeof n.meta.until === 'string') words.untilText = dayjs(n.meta.until).format('DD.MM HH:mm');
  // Telephony notices: the number grouped, the attempts as a count (plural), a closed item's own wording.
  if (typeof n.meta.numberE164 === 'string') words.numberText = formatPhoneE164(n.meta.numberE164);
  if (typeof n.meta.startedAt === 'string') words.startedText = dayjs(n.meta.startedAt).format('HH:mm');
  if (n.kind === 'missed-call') {
    words.count = typeof n.meta.attempts === 'number' ? n.meta.attempts : 1;
    if (n.meta.state === 'closed') words.context = 'closed';
  }
  // "1 iepirkuma darījums" / "2 iepirkuma darījumi": the plural follows the purchase count.
  if (n.kind === 'deal-docs-missing' && typeof n.meta.purchases === 'number') words.count = n.meta.purchases;
  return words;
}

function when(at: string, t: (k: string, o?: Record<string, unknown>) => string): string {
  const d = dayjs(at);
  const minutes = dayjs().diff(d, 'minute');
  if (minutes < 60) return t('ago.minutes', { count: Math.max(1, minutes) });
  if (d.isSame(dayjs(), 'day')) return d.format('HH:mm');
  if (d.isSame(dayjs().subtract(1, 'day'), 'day')) return t('ago.yesterday');
  return d.format('DD.MM');
}

export function NotificationBell(): React.JSX.Element {
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const unread = useQuery({
    queryKey: keys.unread,
    queryFn: async () => (await fetchPage(null, 1)).unread,
    refetchInterval: 60_000,
    meta: QUIET,
  });
  const list = useInfiniteQuery({
    queryKey: keys.list,
    queryFn: ({ pageParam }) => fetchPage(pageParam, PAGE),
    initialPageParam: null as Cursor | null,
    getNextPageParam: (last): Cursor | undefined => {
      const tail = last.notifications.at(-1);
      return last.notifications.length < PAGE || tail === undefined ? undefined : { at: tail.createdAt, id: tail.id };
    },
    enabled: open,
    meta: QUIET,
  });
  const refresh = (): void => void queryClient.invalidateQueries({ queryKey: keys.all });
  const markOne = useMutation({
    meta: QUIET,
    mutationFn: (id: string) => apiClient.post(`/notifications/${id}/read`),
    onSuccess: refresh,
  });
  const markAll = useMutation({
    meta: QUIET,
    mutationFn: () => apiClient.post('/notifications/read-all'),
    onSuccess: refresh,
  });
  const items = list.data?.pages.flatMap((p) => p.notifications) ?? [];
  const count = unread.data ?? 0;

  const content = (
    <Card
      size="small"
      style={{ width: 380 }}
      styles={{ body: { padding: 0 } }}
      role="dialog"
      aria-label={t('title')}
    >
      <Flex justify="space-between" align="center" style={{ padding: '8px 12px' }}>
        <Typography.Text strong>{t('title')}</Typography.Text>
        <Button
          size="small"
          type="link"
          disabled={count === 0}
          loading={markAll.isPending}
          onClick={() => markAll.mutate()}
        >
          {t('markAll')}
        </Button>
      </Flex>
      {list.isError ? (
        <Flex vertical align="center" gap={8} style={{ padding: 16 }}>
          <Typography.Text type="danger">{t('loadError')}</Typography.Text>
          <Button size="small" onClick={() => void list.refetch()}>
            {t('tryAgain')}
          </Button>
        </Flex>
      ) : items.length === 0 && !list.isLoading ? (
        <Empty
          style={{ padding: 16 }}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('empty')}
        />
      ) : (
        <div style={{ maxHeight: 420, overflowY: 'auto' }} role="list">
          {items.map((n) => {
            const href = hrefOf(n);
            return (
              <div key={n.id} role="listitem">
                <button
                  type="button"
                  onClick={() => {
                    if (n.readAt === null) markOne.mutate(n.id);
                    setOpen(false);
                    // A worklist link (R18) replaces whatever search / filters / page the list already had.
                    if (href !== null) {
                      navigate(href, n.kind === 'deal-docs-missing' ? { state: FRESH_WORKLIST_STATE } : undefined);
                    }
                  }}
                  style={{
                    all: 'unset',
                    display: 'block',
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '8px 12px',
                    cursor: href !== null ? 'pointer' : 'default',
                    borderTop: '1px solid rgba(128,128,128,0.15)',
                  }}
                >
                  <Flex justify="space-between" gap={8}>
                    <Typography.Text strong={n.readAt === null}>
                      {n.readAt === null ? (
                        <span style={VISUALLY_HIDDEN}>{t('unreadPrefix')} </span>
                      ) : null}
                      {t(`kind.${n.kind}.title`, { ...wordsOf(n), defaultValue: n.kind })}
                    </Typography.Text>
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: 12, whiteSpace: 'nowrap' }}
                    >
                      {when(n.createdAt, t)}
                    </Typography.Text>
                  </Flex>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t(`kind.${n.kind}.text`, { ...wordsOf(n), defaultValue: '' })}
                  </Typography.Text>
                </button>
              </div>
            );
          })}
        </div>
      )}
      {list.hasNextPage ? (
        <Flex justify="center" style={{ padding: 8 }}>
          <Button
            size="small"
            loading={list.isFetchingNextPage}
            onClick={() => void list.fetchNextPage()}
          >
            {t('older')}
          </Button>
        </Flex>
      ) : null}
    </Card>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomRight"
      content={content}
      arrow={false}
    >
      <Badge count={count} size="small" overflowCount={99}>
        <Button
          type="text"
          icon={<BellOutlined />}
          aria-label={count > 0 ? t('bellUnread', { count }) : t('bell')}
        />
      </Badge>
    </Popover>
  );
}
